// client/src/buildBallUvPolylineFromCv.ts
export type UvPointWithMs = {
  ms: number;
  u: number;
  v: number;
  radiusV?: number | null;
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function pushPointIfValid(
  out: UvPointWithMs[],
  point: Partial<UvPointWithMs> | null | undefined,
) {
  if (!point) return;
  if (!isFiniteNumber(point.ms)) return;
  if (!isFiniteNumber(point.u)) return;
  if (!isFiniteNumber(point.v)) return;

  out.push({
    ms: point.ms,
    u: point.u,
    v: point.v,
    radiusV: isFiniteNumber(point.radiusV) ? point.radiusV : null,
  });
}

function dedupeNearSequentialPoints(
  pts: UvPointWithMs[],
  minDeltaUv = 0.0008,
): UvPointWithMs[] {
  if (pts.length <= 1) return pts;

  const out: UvPointWithMs[] = [pts[0]];

  for (let i = 1; i < pts.length; i += 1) {
    const prev = out[out.length - 1];
    const curr = pts[i];

    const du = curr.u - prev.u;
    const dv = curr.v - prev.v;
    const d2 = du * du + dv * dv;

    if (d2 >= minDeltaUv * minDeltaUv || curr.ms !== prev.ms) {
      out.push(curr);
    }
  }

  return out;
}

function sortByMs(pts: UvPointWithMs[]): UvPointWithMs[] {
  return [...pts].sort((a, b) => a.ms - b.ms);
}

export function extractBallUvSamplesForTimeRange(args: {
  cvJson: any | null | undefined;
  startMs: number;
  endMs: number;
}): UvPointWithMs[] {
  const { cvJson, startMs, endMs } = args;

  if (!cvJson?.sessions?.length) return [];
  if (!isFiniteNumber(startMs) || !isFiniteNumber(endMs)) return [];

  const minMs = Math.min(startMs, endMs);
  const maxMs = Math.max(startMs, endMs);

  const out: UvPointWithMs[] = [];

  for (const session of cvJson.sessions ?? []) {
    for (const rally of session?.rallies ?? []) {
      for (const frame of rally?.frames ?? []) {
        const ms = frame?.ms;

        if (!isFiniteNumber(ms)) continue;
        if (ms < minMs || ms > maxMs) continue;
        // if (false) {}

        const ball = frame?.actions?.ball;
        if (
          ball &&
          isFiniteNumber(ball.u) &&
          isFiniteNumber(ball.v)
        ) {
          pushPointIfValid(out, {
            ms,
            u: ball.u,
            v: ball.v,
            radiusV: ball?.radius_v,
          });
          continue;
        }

        // Optional fallback if your cv sometimes stores the main visible ball
        // under a slightly different key.
        const altBall = frame?.ball;
        if (
          altBall &&
          isFiniteNumber(altBall.u) &&
          isFiniteNumber(altBall.v)
        ) {
          pushPointIfValid(out, {
            ms,
            u: altBall.u,
            v: altBall.v,
            radiusV: altBall?.radius_v,
          });
        }
      }
    }
  }

  return dedupeNearSequentialPoints(sortByMs(out));
}

export function buildBallUvPolylineForTimeRange(args: {
  cvJson: any | null | undefined;
  startMs: number;
  endMs: number;
}): string | null {
  const pts = extractBallUvSamplesForTimeRange(args);
  if (pts.length < 2) return null;
  return pts.map((p) => `${p.u},${p.v}`).join(" ");
}

export function buildBallUvCirclesForTimeRange(args: {
  cvJson: any | null | undefined;
  startMs: number;
  endMs: number;
}): UvPointWithMs[] {
  return extractBallUvSamplesForTimeRange(args);
}

export type UvPointWithFrame = {
  frameOffset: number;
  u: number;
  v: number;
  radiusV?: number | null;
};

export function findCvRallyIndexForStartMs(args: {
  cvJson: any | null | undefined;
  rallyStartMs: number;
  toleranceMs?: number;
}): number | null {
  const { cvJson, rallyStartMs, toleranceMs = 1500 } = args;
  const fps = cvJson?.camera?.fps;
  const rallies = cvJson?.sessions?.[0]?.rallies;
  if (!isFiniteNumber(fps) || fps <= 0 || !Array.isArray(rallies)) return null;
  if (!isFiniteNumber(rallyStartMs)) return null;

  let bestIndex: number | null = null;
  let bestDeltaMs = Number.POSITIVE_INFINITY;
  rallies.forEach((rally: any, index: number) => {
    const frameIndex = rally?.frame_index;
    if (!isFiniteNumber(frameIndex)) return;
    const cvStartMs = (frameIndex / fps) * 1000;
    const deltaMs = Math.abs(cvStartMs - rallyStartMs);
    if (deltaMs < bestDeltaMs) {
      bestIndex = index;
      bestDeltaMs = deltaMs;
    }
  });

  return bestDeltaMs <= toleranceMs ? bestIndex : null;
}

export function extractBallUvSamplesForAbsoluteShotWindow(args: {
  cvJson: any | null | undefined;
  rallyStartMs: number;
  shotStartMs: number;
  shotEndMs: number;
}): {
  cvRallyIndex: number;
  startFrameOffset: number;
  endFrameOffset: number;
  points: UvPointWithFrame[];
} | null {
  const { cvJson, rallyStartMs, shotStartMs, shotEndMs } = args;
  const fps = cvJson?.camera?.fps;
  if (!isFiniteNumber(fps) || fps <= 0) return null;

  const cvRallyIndex = findCvRallyIndexForStartMs({ cvJson, rallyStartMs });
  if (cvRallyIndex == null) return null;
  const rally = cvJson?.sessions?.[0]?.rallies?.[cvRallyIndex];
  const rallyFrameIndex = rally?.frame_index;
  if (!isFiniteNumber(rallyFrameIndex)) return null;

  const firstShotMs = Math.min(shotStartMs, shotEndMs);
  const lastShotMs = Math.max(shotStartMs, shotEndMs);
  const startFrameOffset = Math.max(
    0,
    Math.floor((firstShotMs / 1000) * fps - rallyFrameIndex),
  );
  const endFrameOffset = Math.max(
    startFrameOffset,
    Math.ceil((lastShotMs / 1000) * fps - rallyFrameIndex),
  );
  const points = extractBallUvSamplesForRally({ cvJson, rallyIndex: cvRallyIndex }).filter(
    (point) =>
      point.frameOffset >= startFrameOffset && point.frameOffset <= endFrameOffset,
  );

  return { cvRallyIndex, startFrameOffset, endFrameOffset, points };
}

// function isFiniteNumber(value: unknown): value is number {
//   return typeof value === "number" && Number.isFinite(value);
// }

export function extractBallUvSamplesForRally(args: {
  cvJson: any | null | undefined;
  rallyIndex: number;
}): UvPointWithFrame[] {
  const { cvJson, rallyIndex } = args;

  const rally =
    cvJson?.sessions?.[0]?.rallies?.[rallyIndex];

  if (!rally?.frames?.length) return [];

  const out: UvPointWithFrame[] = [];

  rally.frames.forEach((frame: any, frameOffset: number) => {
    const ball =
      frame?.actions?.ball ??
      frame?.ball ??
      null;

    if (
      ball &&
      isFiniteNumber(ball.u) &&
      isFiniteNumber(ball.v)
    ) {
      out.push({
        frameOffset,
        u: ball.u,
        v: ball.v,
        radiusV: isFiniteNumber(ball?.radius_v) ? ball.radius_v : null,
      });
    }
  });

  return out;
}

export function buildBallUvPolylineForRally(args: {
  cvJson: any | null | undefined;
  rallyIndex: number;
}): string | null {
  const pts = extractBallUvSamplesForRally(args);
  if (pts.length < 2) return null;
  return pts.map((p) => `${p.u},${p.v}`).join(" ");
}

export function extractBallUvSamplesForRallyShotWindow(args: {
  cvJson: any | null | undefined;
  rallyIndex: number;
  shotStartMs: number;
  shotEndMs: number;
  rallyStartMs: number;
  rallyEndMs: number;
}): UvPointWithFrame[] {
  const {
    cvJson,
    rallyIndex,
    shotStartMs,
    shotEndMs,
    rallyStartMs,
    rallyEndMs,
  } = args;

  const rally = cvJson?.sessions?.[0]?.rallies?.[rallyIndex];
  const rallyPts = extractBallUvSamplesForRally({
    cvJson,
    rallyIndex,
  });

  if (!rallyPts.length) return [];
  if (
    !isFiniteNumber(shotStartMs) ||
    !isFiniteNumber(shotEndMs) ||
    !isFiniteNumber(rallyStartMs) ||
    !isFiniteNumber(rallyEndMs)
  ) {
    return rallyPts;
  }

  const rallyDuration = rallyEndMs - rallyStartMs;
  if (rallyDuration <= 0) return rallyPts;

  const startT = Math.max(0, Math.min(1, (shotStartMs - rallyStartMs) / rallyDuration));
  const endT = Math.max(0, Math.min(1, (shotEndMs - rallyStartMs) / rallyDuration));

  const minT = Math.min(startT, endT);
  const maxT = Math.max(startT, endT);

  const lastFrameOffset = Math.max(1, (rally?.frames?.length ?? 1) - 1);

  return rallyPts.filter((pt) => {
    const t = pt.frameOffset / lastFrameOffset;
    return t >= minT && t <= maxT;
  });
}

export function buildBallUvPolylineForRallyShotWindow(args: {
  cvJson: any | null | undefined;
  rallyIndex: number;
  shotStartMs: number;
  shotEndMs: number;
  rallyStartMs: number;
  rallyEndMs: number;
}): string | null {
  const pts = extractBallUvSamplesForRallyShotWindow(args);
  if (pts.length < 2) return null;
  return pts.map((p) => `${p.u},${p.v}`).join(" ");
}
